import { AUDIT_CATEGORIES } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { FilterSelect } from '../../components/filters/FilterSelect';
import { FormPanel } from '../../components/FormPanel';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listAudit, listBranches } from '../../data/fetchers';
import { branchDisplayLabel } from '../../lib/displayIdentity';
import { fmtDate } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import type { AuditAction, AuditEntry } from '../../types/domain';
import { SettingsHeader } from './SettingsHeader';

const ACTION_TH: Record<AuditAction, string> = {
  login: 'เข้าระบบ',
  logout: 'ออกระบบ',
  'void-bill': 'ยกเลิกบิล',
  'price-edit': 'แก้ราคา',
  'product-create': 'เพิ่มสินค้า',
  'stock-adjust': 'ปรับสต๊อก',
  'transfer-create': 'สร้างใบโอน',
  'discount-applied': 'ใช้ส่วนลด',
  'permission-change': 'แก้สิทธิ์',
  'round-close': 'ปิดรอบ',
  'permission-denied': 'ปฏิเสธสิทธิ์',
  'device-management': 'จัดการเครื่อง POS',
  'master-data': 'แก้ไขข้อมูลหลัก',
};

// The API can only ever emit the coarse categories (mapApiAuditAction). The
// remaining legacy actions cannot be emitted by the API, so do not offer filters
// that would always return an empty result.
const API_ACTIONS = new Set<string>(AUDIT_CATEGORIES);

const ACTION_OPTIONS = [
  { value: '', label: 'ทั้งหมด' },
  ...(Object.keys(ACTION_TH) as AuditAction[])
    .filter((a) => API_ACTIONS.has(a))
    .map((a) => ({
      value: a,
      label: ACTION_TH[a],
    })),
];

const PAGE_SIZE = 8;

export function AuditLogView() {
  const user = useSession((s) => s.user);
  const branchId = useUi((s) => s.branchId);
  const [action, setAction] = useState<AuditAction | ''>('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [branchId]); // changing the global branch resets to page 1

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const allowedBranchIds = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];

  const { data, isPending } = useQuery({
    queryKey: ['audit', branchId, action, page, allowedBranchIds],
    queryFn: () =>
      listAudit({
        page,
        pageSize: PAGE_SIZE,
        branchId,
        action: action || undefined,
        allowedBranchIds,
      }),
    enabled: !!user && branches.length > 0,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<ColumnDef<AuditEntry, unknown>[]>(
    () => [
      {
        id: 'at',
        header: 'เวลา',
        cell: (c) => <span className="num">{fmtDate(c.row.original.at)}</span>,
      },
      { accessorKey: 'user', header: 'ผู้ใช้' },
      {
        id: 'branch',
        header: 'สาขา',
        accessorFn: (row) =>
          row.branchId === 'global'
            ? 'ทั้งร้าน'
            : branchDisplayLabel(
                branches.find((branch) => branch.id === row.branchId),
              ),
      },
      {
        id: 'action',
        header: 'กิจกรรม',
        cell: (c) => (
          <Pill tone="primary">{ACTION_TH[c.row.original.action]}</Pill>
        ),
      },
      {
        accessorKey: 'detail',
        header: 'รายละเอียด',
        cell: ({ row }) => (
          <>
            <div>{row.original.detail}</div>
            {row.original.references && (
              <details>
                <summary className="cursor-pointer">ข้อมูลทางเทคนิค</summary>
                <dl className="break-all text-sm-label text-ink-3">
                  <dt>ประเภทข้อมูล</dt>
                  <dd>{row.original.references.entity}</dd>
                  <dt>รหัสอ้างอิง</dt>
                  <dd>{row.original.references.entityId}</dd>
                  <dt>ID ผู้ใช้ภายใน</dt>
                  <dd>{row.original.references.actorUserId}</dd>
                  {row.original.references.branchId && (
                    <>
                      <dt>ID สาขาภายใน</dt>
                      <dd>{row.original.references.branchId}</dd>
                    </>
                  )}
                </dl>
              </details>
            )}
          </>
        ),
      },
    ],
    [branches],
  );

  const loading = isPending || !user || branches.length === 0;

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="ประวัติการใช้งาน"
        subtitle="ตรวจสอบกิจกรรมสำคัญ การเปลี่ยนแปลงข้อมูล และเหตุการณ์ด้านสิทธิ์"
        status={{
          label: loading ? 'กำลังโหลดประวัติ' : `${total} รายการในผลลัพธ์`,
          tone: loading ? 'neutral' : 'ok',
        }}
      />
      <FormPanel className="mb-6 max-w-md">
        <FilterSelect
          label="กรองตามกิจกรรม"
          value={action}
          onChange={(v) => {
            setAction(v as AuditAction | '');
            setPage(1);
          }}
          options={ACTION_OPTIONS}
        />
      </FormPanel>
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-10 rounded-card bg-line-2 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <>
          <DataTable columns={columns} data={rows} />
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm-label text-ink-3">
              หน้า {page} / {lastPage}
            </span>
            <div className="flex gap-2">
              <Btn
                kind="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </Btn>
              <Btn
                kind="ghost"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                ถัดไป
              </Btn>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
